// Inject-driven simulation: "They Don't Want Me Here".
// Model: EVENT -> CHILD ACTS -> AI INTERPRETS -> WORLD REACTS -> NEXT INJECT.
// Scenes are pre-authored "injects"; the AI voices characters within safe briefs.

const IMG =
  "https://static.prod-images.emergentagent.com/jobs/62c4d2db-e67b-4f45-8693-2623831d62c9/images";

export const IMAGES = {
  lunch: `${IMG}/c15c828bafa8ea89f6c04d5d28f967355432fdb3834e83af00ab2d2976021323.jpeg`,
  arjun: `${IMG}/4e2127516f09c95a78ce896b266cbaaba237a019f2f4c64e070224e20bb9edc2.jpeg`,
  phone: `${IMG}/10219112eeed98af029bb3f7fb64b4bd42a2a0d4b5f20cc1ec7e45d2d13c2d9a.jpeg`,
  night: `${IMG}/75b60229d86085ac2f548b2a4fe1a65fa717582b446532948df77913b6359f6d.jpeg`,
  teacher: `${IMG}/152242f02d2a83db1e478f2a9c95328cf96c01c1fdcde37db5ff5cd23632814d.jpeg`,
  maya: `${IMG}/d717bfd5668a81652d7326f7d6a407a41ed7349d324c60d0abd29c335d5022a9.jpeg`,
  debrief: `${IMG}/fca75af5bd9b3f24b588c3ce6df48a3aab184471da243e1bb220377785e49b59.jpeg`,
};

// line kinds: { narration } or { speaker, text }
export const SCENES = [
  {
    id: "lunch",
    title: "Lunch Break",
    setting: "the school cafeteria at lunch",
    image: IMAGES.lunch,
    timestamp: "12:34 PM · Lunch Break",
    ambient: "cafeteria",
    mode: "cinematic",
    npc: "Maya",
    prompt: "Your move",
    inject: [
      { narration: "Three of your classmates are sitting together." },
      { speaker: "Maya", text: "We're actually sitting together today. Can you sit somewhere else?" },
      { narration: "Another classmate laughs quietly." },
    ],
    maxTurns: 1,
    brief:
      "Maya and two classmates are excluding the child at lunch. Maya may sound casual, a little dismissive or slightly awkward, but never cruel or threatening. She will not apologise in this scene.",
  },
  {
    id: "pressure",
    title: "Social Pressure",
    setting: "just after the lunch table, another classmate noticed",
    image: IMAGES.arjun,
    timestamp: "12:36 PM · Lunch Break",
    ambient: "cafeteria",
    mode: "cinematic",
    npc: "Arjun",
    prompt: "Your move",
    inject: [
      { narration: "You step away. Arjun, another classmate, saw the whole thing." },
      { speaker: "Arjun", text: "Hey... what happened?" },
    ],
    maxTurns: 1,
    brief:
      "Arjun is a friendly, concerned classmate checking in. He is kind and curious, not pushy, and reacts warmly and supportively to whatever the child shares. Keep it realistic for a 12-13 year old.",
  },
  {
    id: "messages",
    title: "Later That Day",
    setting: "a private text exchange with Maya after school",
    image: IMAGES.phone,
    timestamp: "3:47 PM · Later That Day",
    ambient: "corridor",
    mode: "messaging",
    npc: "Maya",
    contact: "Maya",
    prompt: "Reply",
    inject: [
      { speaker: "Maya", text: "hey" },
      { speaker: "Maya", text: "about lunch..." },
      { speaker: "Maya", text: "don't take it so seriously" },
      { speaker: "Maya", text: "they were only joking" },
    ],
    maxTurns: 3,
    brief:
      "This is a private text exchange. Maya is half-apologetic but defensive, minimising it ('it was just a joke'). She may soften a little if the child is calm, or get slightly defensive if attacked, but never cruel. Keep replies as short, casual, lowercase text messages.",
  },
  {
    id: "groupchat",
    title: "Class Group",
    setting: "a class group chat comment that hints at the child",
    image: IMAGES.night,
    timestamp: "8:12 PM · Class Group",
    ambient: "night",
    mode: "actions",
    npc: "the group chat",
    contact: "Class Group",
    prompt: "What do you do?",
    inject: [
      { speaker: "Class Group", text: "Lunch was dramatic today 😂" },
      { narration: "You understand it might be about you." },
    ],
    actions: [
      { id: "reply", label: "Reply", npc: "the group chat" },
      { id: "ignore", label: "Ignore for now", npc: "narrator" },
      { id: "private", label: "Message someone privately", npc: "a trusted friend" },
      { id: "else", label: "Something else", free: true, npc: "the group chat" },
    ],
    maxTurns: 1,
    brief:
      "This is about a class group-chat comment that hints at the child. Produce a short, realistic consequence of the action the child chose. If they reply in the group, others react mildly and it settles. If they ignore, the chat quietly moves on. If they message someone privately, that person answers kindly. Never escalate to serious bullying; keep it plausible, brief and safe. If narrator, describe the quiet outcome in one short sentence.",
  },
  {
    id: "morning",
    title: "Next Morning",
    setting: "the next morning, a teacher gently checks in",
    image: IMAGES.teacher,
    timestamp: "8:40 AM · Next Morning",
    ambient: "classroom",
    mode: "cinematic",
    npc: "Ms Rivera",
    prompt: "Your move",
    inject: [
      { narration: "The next morning, your teacher catches you before class." },
      { speaker: "Ms Rivera", text: "You seem quieter than usual today. Everything okay?" },
    ],
    maxTurns: 1,
    brief:
      "Ms Rivera is a kind teacher gently checking in. She is warm, patient and non-judgemental, and responds supportively to whatever the child shares, offering gentle reassurance. Realistic and brief.",
  },
  {
    id: "dilemma",
    title: "The Dilemma",
    setting: "Maya approaches privately, quietly",
    image: IMAGES.maya,
    timestamp: "8:52 AM · Before Class",
    ambient: "corridor",
    mode: "cinematic",
    npc: "Maya",
    prompt: "Your move",
    last: true,
    inject: [
      { narration: "Maya catches you alone in the corridor." },
      { speaker: "Maya", text: "I'm sorry about yesterday." },
      { speaker: "Maya", text: "I should've said something." },
      { speaker: "Maya", text: "But please don't tell the teacher. They'll make it into a huge thing." },
    ],
    maxTurns: 1,
    brief:
      "Maya privately apologises but asks the child not to tell the teacher. She is sincere but anxious about consequences. She responds realistically to the child's decision — relieved if reassured, worried if the child insists on telling — but never threatening. This is the final moment.",
  },
];

// Text the narrator reads for a scene's inject (narration + dialogue).
export const buildInjectNarration = (scene) =>
  scene.inject
    .map((l) => (l.narration ? l.narration : l.text))
    .join("  ")
    .trim();
